import React, { useState } from "react";
import axios from "axios";
import { useI18n } from "../i18n/I18nProvider";
import { CONTACT } from "../constants/testIds";
import { Reveal } from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";
import { openWhatsapp, GAELON_WHATSAPP_DISPLAY } from "../lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const buildWhatsAppMessage = () => {
    const lines = [];
    if (locale === "pt-br") {
      lines.push(`Olá GAELON, sou ${form.name}.`);
      if (form.company) lines.push(`Empresa: ${form.company}`);
      lines.push(`E-mail: ${form.email}`);
      lines.push("");
      lines.push(form.message);
    } else if (locale === "es") {
      lines.push(`Hola GAELON, soy ${form.name}.`);
      if (form.company) lines.push(`Empresa: ${form.company}`);
      lines.push(`Correo: ${form.email}`);
      lines.push("");
      lines.push(form.message);
    } else if (locale === "zh") {
      lines.push(`您好 GAELON,我是 ${form.name}。`);
      if (form.company) lines.push(`公司:${form.company}`);
      lines.push(`邮箱:${form.email}`);
      lines.push("");
      lines.push(form.message);
    } else {
      lines.push(`Hello GAELON, I'm ${form.name}.`);
      if (form.company) lines.push(`Company: ${form.company}`);
      lines.push(`Email: ${form.email}`);
      lines.push("");
      lines.push(form.message);
    }
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("sending");
    setErrMsg("");
    // Fire lead capture (non-blocking) then hand off to WhatsApp.
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
    } catch (_err) {
      // Even if backend logging fails, still open WhatsApp so the customer isn't blocked.
    }
    openWhatsapp(locale, buildWhatsAppMessage());
    setStatus("success");
    setForm({ name: "", email: "", company: "", message: "" });
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative bg-black border-t border-white/5 px-6 md:px-10 lg:px-14 py-32 md:py-44"
    >
      <div className="mx-auto max-w-[1440px] grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-24">
        <Reveal>
          <div>
            <div className="flex items-center gap-4 text-[10px] tracking-[0.4em] uppercase font-mono text-white/50">
              <span className="w-8 h-px bg-white/30" />
              {t.contact.eyebrow}
            </div>
            <h2 className="mt-8 font-display tracking-display text-white text-[34px] sm:text-[44px] md:text-[56px] lg:text-[64px] leading-[1.02] font-medium">
              {t.contact.title}
            </h2>
            <p className="mt-8 max-w-md text-white/60 text-[15px] leading-[1.65]">
              {t.contact.sub}
            </p>

            <div className="mt-14 flex flex-col gap-3 font-mono text-[11px] tracking-[0.22em] uppercase text-white/45">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                <a
                  href="https://wa.me/5511914479621"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  WhatsApp · {GAELON_WHATSAPP_DISPLAY}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-white/60 rounded-full" />
                <a
                  href="mailto:contato@gaelon.com.br"
                  className="hover:text-white transition-colors"
                >
                  contato@gaelon.com.br
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-white/50 rounded-full" />
                <a
                  href="mailto:sac@gaelon.com.br"
                  className="hover:text-white transition-colors"
                >
                  sac@gaelon.com.br
                </a>
                <span className="text-white/35 normal-case tracking-normal">(SAC)</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full" />
                <span>São Paulo · Brasil</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid={CONTACT.form}
            onSubmit={submit}
            className="bg-[#040404] border border-white/10 p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div>
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                  {t.contact.name}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  data-testid={CONTACT.name}
                  className="gaelon-input w-full"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                  {t.contact.email}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid={CONTACT.email}
                  className="gaelon-input w-full"
                  autoComplete="email"
                />
              </div>
              <div className="md:col-span-2 mt-6">
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                  {t.contact.company}
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  data-testid={CONTACT.company}
                  className="gaelon-input w-full"
                  autoComplete="organization"
                />
              </div>
              <div className="md:col-span-2 mt-6">
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                  {t.contact.message}
                </label>
                <textarea
                  required
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid={CONTACT.message}
                  className="gaelon-input w-full resize-none"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 justify-between">
              <MagneticButton
                type="submit"
                variant="primary"
                data-testid={CONTACT.submit}
                disabled={status === "sending"}
              >
                {status === "sending" ? t.contact.submitting : t.contact.submit}
              </MagneticButton>

              {status === "success" && (
                <span
                  data-testid={CONTACT.success}
                  className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/80"
                >
                  ✓ {t.contact.success}
                </span>
              )}
              {status === "error" && (
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/60">
                  {errMsg}
                </span>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
